import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, ChevronLeft, ChevronRight, Star, AlertTriangle, Download, Search, Building2, Shield, ArrowRight, DollarSign } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import SectionFadeIn from "@/components/SectionFadeIn";
import TrustBar from "@/components/home/TrustBar";
import LL11NewsSection from "@/components/home/LL11NewsSection";
import ServicesGrid from "@/components/home/ServicesGrid";
import HowItWorks from "@/components/home/HowItWorks";
import FAQSection from "@/components/home/FAQSection";
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useToast } from "@/hooks/use-toast";
import { readStoredUtm, trackContactFormSubmit } from "@/lib/tracking";
import universityPlaceImg from "@/assets/projects/thumbs/1-university-place.webp";
import henryStreetImg from "@/assets/projects/thumbs/100-henry-street.webp";
import riversideDriveImg from "@/assets/projects/thumbs/765-riverside-drive.webp";
import madisonAveImg from "@/assets/projects/thumbs/271-madison-avenue.webp";
import east72ndImg from "@/assets/projects/thumbs/311-east-72nd-street.webp";
import williamStreetImg from "@/assets/projects/thumbs/62-william-street.webp";

const projectImages = [universityPlaceImg, henryStreetImg, riversideDriveImg, madisonAveImg, east72ndImg, williamStreetImg];
const heroImage = "/hero-nyc.webp";
const heroImageMobile = "/hero-nyc-mobile.webp";

const Index = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [checkAddress, setCheckAddress] = useState("");
  const [checkBorough, setCheckBorough] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const [submittingContact, setSubmittingContact] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 2);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('home');
  const prefix = i18n.language === 'es' ? '/es' : '';

  const testimonialItems = t('testimonials.items', { returnObjects: true }) as Array<{ quote: string; author: string; role: string }>;
  const testimonials = Array.isArray(testimonialItems) ? testimonialItems : [];

  const projectItems = t('featuredProjects.items', { returnObjects: true }) as Array<{ title: string; category: string }>;
  const projects = Array.isArray(projectItems) ? projectItems.map((p, i) => ({ ...p, image: projectImages[i] })) : [];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setSubmittingContact(true);

    try {
      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.get("name"),
          email: formData.get("email"),
          phone: formData.get("phone"),
          service: "Website assessment request",
          message: formData.get("message"),
          source: "homepage-contact-form",
          ...readStoredUtm(),
        }),
      });

      if (!response.ok) throw new Error("Request failed");
      trackContactFormSubmit("homepage_contact_form");
      toast({ title: t('contactCta.toast.title'), description: t('contactCta.toast.description') });
      form.reset();
    } catch {
      toast({
        title: "ARCONDES",
        description: i18n.language === "es" ? "No pudimos enviar la solicitud. Llame al 347-613-1717." : "We could not send the request. Please call 347-613-1717.",
        variant: "destructive",
      });
    } finally {
      setSubmittingContact(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="NYC Building Inspections, Facade Compliance & Engineering | ARCONDES"
        description="Facade inspections (FISP/Local Law 11), parapet assessments, leak investigations, structural repairs, DOB violation removal, and technical reports for NYC buildings. Serving all 5 boroughs. Free assessment — 347-613-1717."
        canonical="https://www.arcondes.com/"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "ARCONDES",
            "description": "Building Inspections, Facade Compliance & Construction Solutions in NYC",
            "url": "https://www.arcondes.com",
            "telephone": "+1-347-613-1717",
            "email": "info@arcondes.com",
            "areaServed": [
              { "@type": "City", "name": "New York", "addressRegion": "NY" },
              { "@type": "Place", "name": "Long Island", "addressRegion": "NY" }
            ],
            "address": { "@type": "PostalAddress", "streetAddress": "224 W 35th St Ste 500 #2822", "addressLocality": "New York", "addressRegion": "NY", "postalCode": "10001", "addressCountry": "US" },
            "geo": { "@type": "GeoCoordinates", "latitude": 40.7516, "longitude": -73.9890 },
            "serviceType": ["Facade Inspection", "Building Inspection", "Leak Investigation", "Structural Repairs", "Reports & Documentation", "DOB Violations Removal"],
            "priceRange": "$$"
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is Local Law 11 / FISP?", "acceptedAnswer": { "@type": "Answer", "text": "Local Law 11, now known as the Facade Inspection & Safety Program (FISP), requires all NYC buildings taller than 6 stories to have their exterior walls inspected every 5 years by a Qualified Exterior Wall Inspector (QEWI)." }},
              { "@type": "Question", "name": "How much does a facade inspection cost?", "acceptedAnswer": { "@type": "Answer", "text": "Costs vary depending on building size, accessibility, and scope. We provide free initial assessments and transparent, upfront pricing before any work begins." }},
              { "@type": "Question", "name": "What happens if I miss my FISP filing deadline?", "acceptedAnswer": { "@type": "Answer", "text": "DOB lists a $1,000 monthly late-filing penalty and a separate $5,000 annual failure-to-file penalty after the filing window; other penalties depend on the building's conditions and compliance history." }},
              { "@type": "Question", "name": "Do you handle DOB filings and permits?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. We manage the entire DOB filing process, including FISP report submission, permit applications, violation resolution, and certificate of correction filings." }},
              { "@type": "Question", "name": "What areas do you serve?", "acceptedAnswer": { "@type": "Answer", "text": "We serve all five NYC boroughs — Manhattan, Brooklyn, Queens, The Bronx, and Staten Island — as well as Long Island." }}
            ]
          }
        ]}
      />

      {/* Hero */}
      <section className="relative min-h-[75vh] flex items-center">
          <div className="absolute inset-0">
            <picture className="block w-full h-full">
              <source media="(max-width: 640px)" srcSet={heroImageMobile} type="image/webp" />
              <img
                src={heroImage}
                alt="New York City skyline"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                width={1200}
                height={675}
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/85 via-foreground/60 to-transparent" />
          </div>
        <div className="relative container-max px-4 sm:px-6 lg:px-8 py-20">
          <SectionFadeIn>
            <div className="max-w-2xl">
              {/* Urgency badge */}
              <div className="inline-flex items-center gap-2 bg-danger/20 text-white px-4 py-2 rounded-sm text-xs font-semibold mb-6 border border-danger/40 backdrop-blur-sm">
                <AlertTriangle className="h-3.5 w-3.5 text-danger" />
                {t('hero.urgencyBadge')}
              </div>
              <div className="h-1 w-16 bg-brand-yellow mb-6" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6">
                {t('hero.title').split('\n').map((line, i, arr) => (
                  <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                ))}
              </h1>
              <p className="text-lg text-primary-foreground/80 mb-8 max-w-xl leading-relaxed">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-sm tracking-wider font-semibold px-8">
                  <Link to={`${prefix}/building-report`}><Search className="h-4 w-4 mr-2" />{t('hero.ctaReport')}</Link>
                </Button>
                <Button asChild size="lg" className="bg-primary-foreground text-foreground hover:bg-primary-foreground/90 uppercase text-sm tracking-wider font-semibold px-8">
                  <a href="tel:+13476131717"><Phone className="h-4 w-4 mr-2" />{t('hero.ctaCall')}</a>
                </Button>
              </div>
            </div>
          </SectionFadeIn>
        </div>
      </section>

      {/* Put services and trust signals before self-service tools so visitors can choose a direct path first. */}
      <TrustBar />
      <ServicesGrid />

      {/* Tools Slider */}
      <section className="relative bg-[#0a0a0a] py-16 sm:py-20 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-yellow to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(245,166,35,0.06)_0%,_transparent_70%)]" />
        <div className="relative container-max px-4 sm:px-6 lg:px-8">
          {/* Slide indicators */}
          <div className="flex justify-center gap-2 mb-8">
            {[0, 1].map((i) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                aria-label={`Show tool slide ${i + 1}`}
                aria-current={activeSlide === i ? "true" : undefined}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  activeSlide === i ? "w-8 bg-brand-yellow" : "w-4 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>

          {/* Slides container */}
          <div className="relative min-h-[280px]">
          {/* Slide 0: Building Report */}
          <div className={`transition-all duration-700 ease-in-out absolute inset-0 ${activeSlide === 0 ? "opacity-100 scale-100 z-10" : "opacity-0 scale-95 z-0 pointer-events-none"}`}>
            <SectionFadeIn>
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-brand-yellow/15 text-brand-yellow px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-widest mb-4 border border-brand-yellow/30">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Free Instant Lookup
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  Does Your Building Have <span className="text-brand-yellow">Violations</span>?
                </h2>
                <p className="text-white/60 mt-2 text-sm sm:text-base max-w-xl mx-auto">
                  Check your building's DOB compliance status in seconds. Enter your address below.
                </p>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (checkAddress.trim() && checkBorough) {
                    navigate(`/building-report?address=${encodeURIComponent(checkAddress)}&borough=${encodeURIComponent(checkBorough)}`);
                  }
                }}
                className="max-w-3xl mx-auto"
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <input
                      type="text"
                      placeholder="Enter building address..."
                      value={checkAddress}
                      onChange={(e) => setCheckAddress(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3.5 bg-white/10 border border-white/20 text-white placeholder:text-white/40 rounded-sm text-sm focus:outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow/50 transition-colors"
                    />
                  </div>
                  <label className="sr-only" htmlFor="borough-select">Borough</label>
                  <select
                    id="borough-select"
                    value={checkBorough}
                    onChange={(e) => setCheckBorough(e.target.value)}
                    required
                    className="sm:w-44 py-3.5 px-4 bg-white/10 border border-white/20 text-white rounded-sm text-sm focus:outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow/50 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" disabled className="bg-[#1a1a1a]">Borough</option>
                    <option value="Manhattan" className="bg-[#1a1a1a]">Manhattan</option>
                    <option value="Brooklyn" className="bg-[#1a1a1a]">Brooklyn</option>
                    <option value="Queens" className="bg-[#1a1a1a]">Queens</option>
                    <option value="Bronx" className="bg-[#1a1a1a]">Bronx</option>
                    <option value="Staten Island" className="bg-[#1a1a1a]">Staten Island</option>
                  </select>
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-sm tracking-wider font-bold px-8 py-3.5 shadow-lg shadow-brand-yellow/20"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Check Now
                  </Button>
                </div>
              </form>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-5 text-xs text-white/50">
                <span className="inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-brand-yellow/70" />Free</span>
                <span className="inline-flex items-center gap-1.5"><Search className="h-3.5 w-3.5 text-brand-yellow/70" />Instant Results</span>
                <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-brand-yellow/70" />NYC DOB Data</span>
              </div>
            </SectionFadeIn>
          </div>

          {/* Slide 1: Violation Fine Calculator */}
          <div className={`transition-all duration-700 ease-in-out absolute inset-0 ${activeSlide === 1 ? "opacity-100 scale-100 z-10" : "opacity-0 scale-95 z-0 pointer-events-none"}`}>
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-red-500/15 text-red-400 px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-widest mb-4 border border-red-500/30">
                <DollarSign className="h-3.5 w-3.5" />
                Free Calculator
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                How Much Are Your Violations <span className="text-red-400">Costing You</span>?
              </h2>
              <p className="text-white/60 mt-2 text-sm sm:text-base max-w-xl mx-auto">
                Estimate potential exposure using published NYC DOB penalty categories.
              </p>
            </div>
            <div className="max-w-md mx-auto">
              <Button
                asChild
                size="lg"
                className="w-full bg-red-600 text-white hover:bg-red-700 uppercase text-sm tracking-wider font-bold px-8 py-4 shadow-lg shadow-red-500/20"
              >
                <Link to={`${prefix}/tools/violation-calculator`}>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Estimate Penalties
                </Link>
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-5 text-xs text-white/50">
              <span className="inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-red-400/70" />Free</span>
              <span className="inline-flex items-center gap-1.5"><Search className="h-3.5 w-3.5 text-red-400/70" />30 Seconds</span>
              <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-red-400/70" />DOB & ECB Fines</span>
            </div>
          </div>

          </div>{/* end slides container */}
        </div>
      </section>

      {/* LL11 / FISP News — compact 3-item strip */}
      <LL11NewsSection compact />

      {/* How It Works */}
      <HowItWorks />

      {/* Stats + Featured Projects (combined) */}
      <section className="bg-foreground py-16">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16 pb-16 border-b border-white/10">
            {[
              { value: t('stats.values.buildings'), label: t('stats.buildingsInspected') },
              { value: t('stats.values.years'), label: t('stats.yearsExperience') },
              { value: t('stats.values.compliance'), label: t('stats.complianceRate') },
              { value: t('stats.values.emergency'), label: t('stats.emergencySupport') },
            ].map((stat) => (
              <SectionFadeIn key={stat.label}>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-brand-yellow mb-2">{stat.value}</div>
                  <div className="text-primary-foreground/60 text-sm uppercase tracking-wider">{stat.label}</div>
                </div>
              </SectionFadeIn>
            ))}
          </div>

          {/* Featured Projects */}
          <SectionFadeIn>
            <div className="flex justify-between items-end mb-12">
              <div>
                <div className="h-1 w-12 bg-brand-yellow mb-4" />
                <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-4">{t('featuredProjects.title')}</h2>
                <p className="text-primary-foreground/60 max-w-xl">{t('featuredProjects.subtitle')}</p>
              </div>
              <Button asChild className="hidden sm:inline-flex bg-transparent border border-white/30 text-primary-foreground hover:bg-white/10 hover:border-brand-yellow hover:text-brand-yellow uppercase text-xs tracking-wider">
                <Link to={`${prefix}/projects`}>{t('featuredProjects.viewAll')}</Link>
              </Button>
            </div>
          </SectionFadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, i) => (
              <SectionFadeIn key={project.title} delay={i * 0.05}>
                <div className="group relative overflow-hidden rounded-sm">
                  <img src={project.image} alt={project.title} width={900} height={600} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/60 transition-colors duration-300 flex items-end">
                    <div className="p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <span className="text-brand-yellow text-xs uppercase tracking-wider font-medium">{project.category}</span>
                      <h3 className="text-primary-foreground text-lg font-semibold mt-1">{project.title}</h3>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-yellow transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </div>
              </SectionFadeIn>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Button asChild className="bg-transparent border border-white/30 text-primary-foreground hover:bg-white/10 hover:border-brand-yellow hover:text-brand-yellow uppercase text-xs tracking-wider">
              <Link to={`${prefix}/projects`}>{t('featuredProjects.viewAllMobile')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Results Banner */}
      <section className="relative bg-brand-yellow py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(0,0,0,0.05) 20px, rgba(0,0,0,0.05) 21px)" }} />
        </div>
        <div className="relative container-max px-4 sm:px-6 lg:px-8">
          <SectionFadeIn>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-brand-yellow-foreground leading-tight max-w-3xl mx-auto">
                {t('resultsBanner.title')}
              </h2>
            </div>
          </SectionFadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
            {[
              { value: t('resultsBanner.metrics.violations.value'), label: t('resultsBanner.metrics.violations.label') },
              { value: t('resultsBanner.metrics.buildings.value'), label: t('resultsBanner.metrics.buildings.label') },
              { value: t('resultsBanner.metrics.filing.value'), label: t('resultsBanner.metrics.filing.label') },
            ].map((metric) => (
              <SectionFadeIn key={metric.label}>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-brand-yellow-foreground mb-2">{metric.value}</div>
                  <div className="text-brand-yellow-foreground/70 text-sm uppercase tracking-wider font-medium">{metric.label}</div>
                </div>
              </SectionFadeIn>
            ))}
          </div>
          <SectionFadeIn>
            <div className="text-center">
              <Button asChild size="lg" className="bg-foreground text-primary-foreground hover:bg-foreground/90 uppercase text-sm tracking-wider font-semibold px-10">
                <Link to={`${prefix}/contact`}>{t('resultsBanner.cta')}</Link>
              </Button>
            </div>
          </SectionFadeIn>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-secondary">
        <div className="container-max">
          <SectionFadeIn>
            <div className="text-center mb-12">
              <div className="h-1 w-12 bg-brand-yellow mx-auto mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('testimonials.title')}</h2>
            </div>
          </SectionFadeIn>
          <SectionFadeIn>
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-brand-yellow text-brand-yellow" />)}
              </div>
              {testimonials.length > 0 && (
                <>
                  <blockquote className="text-lg sm:text-xl text-foreground leading-relaxed mb-6 italic">
                    "{testimonials[currentTestimonial].quote}"
                  </blockquote>
                  <div className="text-sm font-semibold text-foreground">{testimonials[currentTestimonial].author}</div>
                  <div className="text-sm text-muted-foreground">{testimonials[currentTestimonial].role}</div>
                </>
              )}
              <div className="flex justify-center gap-4 mt-8">
                <button onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)} className="p-2 border border-border rounded-sm hover:border-brand-yellow transition-colors" aria-label={t('testimonials.prevLabel')}>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)} className="p-2 border border-border rounded-sm hover:border-brand-yellow transition-colors" aria-label={t('testimonials.nextLabel')}>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </SectionFadeIn>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* Contact CTA + Lead Magnet (combined) */}
      <section className="bg-foreground section-padding pb-24 sm:pb-28">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <SectionFadeIn>
              <div className="h-1 w-12 bg-brand-yellow mb-6" />
              <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-4">{t('contactCta.title')}</h2>
              <p className="text-primary-foreground/70 mb-6 leading-relaxed">{t('contactCta.subtitle')}</p>
              <a href="tel:+13476131717" className="inline-flex items-center gap-2 text-brand-yellow text-lg font-semibold mb-10">
                <Phone className="h-5 w-5" />
                347-613-1717
              </a>

              {/* Lead Magnet integrated */}
              <div className="bg-white/5 border border-white/10 rounded-sm p-6">
                <div className="inline-flex items-center gap-2 bg-brand-yellow/15 text-brand-yellow px-3 py-1 rounded-sm text-xs font-semibold mb-3 border border-brand-yellow/30">
                  {t('leadMagnet.badge')}
                </div>
                <h3 className="text-lg font-bold text-primary-foreground mb-2">{t('leadMagnet.title')}</h3>
                <p className="text-primary-foreground/60 text-sm leading-relaxed mb-4">{t('leadMagnet.subtitle')}</p>
                <Button asChild size="sm" className="bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-xs tracking-wider font-semibold">
                  <Link to={`${prefix}/fisp-guide`}><Download className="h-3.5 w-3.5 mr-2" />{t('leadMagnet.cta')}</Link>
                </Button>
              </div>
            </SectionFadeIn>
            <SectionFadeIn delay={0.1}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input name="name" type="text" autoComplete="name" aria-label={t('contactCta.form.name')} placeholder={t('contactCta.form.name')} required className="w-full px-4 py-3 bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-sm text-sm focus:outline-none focus:border-brand-yellow" />
                  <input name="email" type="email" autoComplete="email" aria-label={t('contactCta.form.email')} placeholder={t('contactCta.form.email')} required className="w-full px-4 py-3 bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-sm text-sm focus:outline-none focus:border-brand-yellow" />
                </div>
                <input name="phone" type="tel" autoComplete="tel" aria-label={t('contactCta.form.phone')} placeholder={t('contactCta.form.phone')} className="w-full px-4 py-3 bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-sm text-sm focus:outline-none focus:border-brand-yellow" />
                <textarea name="message" aria-label={t('contactCta.form.message')} placeholder={t('contactCta.form.message')} rows={4} className="w-full px-4 py-3 bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-sm text-sm focus:outline-none focus:border-brand-yellow resize-none" />
                <Button type="submit" disabled={submittingContact} className="w-full bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-sm tracking-wider font-semibold py-3">
                  {submittingContact ? (i18n.language === "es" ? "Enviando..." : "Sending...") : t('contactCta.form.submit')}
                </Button>
              </form>
            </SectionFadeIn>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
